/**
 * EmDispatchConsole — one-screen dispatch monitor.
 *  Route: /emdispatch/console
 *   • Live feed of open incidents (AI-triaged RED/YELLOW/GREEN)
 *   • Priority + status filters
 *   • Guided call-taking (new incident sheet) with auto-triage on submit
 *   • Timeline + status transitions (ack → dispatch → enroute → arrived → closed)
 *   • Auto-refresh every 15 s
 */
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { emdApi, formatEmdError, emdLogout } from "../../lib/emdispatchApi";
import {
  SirenIcon, Hospital, ArrowClockwise, Plus, X, Phone, MapPin,
  UserCircle, Clock, ShieldCheck, Warning, FirstAid, Truck, Fire,
  SignOut, ChatCircleText, CheckCircle, Microphone, Stop,
} from "@phosphor-icons/react";

const PRIO = {
  RED:    { fg: "#FFFFFF", bg: "#DC2626", label: "RED · Critical", Icon: Fire },
  YELLOW: { fg: "#78350F", bg: "#FBBF24", label: "YELLOW · Urgent", Icon: Warning },
  GREEN:  { fg: "#065F46", bg: "#A7F3D0", label: "GREEN · Routine", Icon: ShieldCheck },
};

const STATUS_LABELS = {
  open: "Open", acknowledged: "Ack'd", dispatched: "Dispatched",
  enroute: "En-route", arrived: "Arrived", closed: "Closed", cancelled: "Cancelled",
};

const NEXT_STATUS = {
  open: "acknowledged",
  acknowledged: "dispatched",
  dispatched: "enroute",
  enroute: "arrived",
  arrived: "closed",
};

const NEXT_LABEL = {
  acknowledged: "Acknowledge",
  dispatched: "Dispatch",
  enroute: "Mark en-route",
  arrived: "Mark arrived",
  closed: "Close incident",
};

export default function EmDispatchConsole() {
  const [me, setMe] = useState(null);
  const [incidents, setIncidents] = useState([]);
  const [stats, setStats] = useState(null);
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("active");
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const nav = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (priorityFilter !== "all") params.priority = priorityFilter;
      if (statusFilter !== "active" && statusFilter !== "all") params.status = statusFilter;
      const [inc, dash] = await Promise.all([
        emdApi.get("/emdispatch/incidents", { params }),
        emdApi.get("/emdispatch/dashboard"),
      ]);
      let list = inc.data.incidents || [];
      if (statusFilter === "active") {
        list = list.filter(i => !["closed", "cancelled"].includes(i.status));
      }
      setIncidents(list);
      setStats(dash.data);
    } catch (e) { toast.error(formatEmdError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    emdApi.get("/emdispatch/me")
      .then(r => setMe(r.data))
      .catch(() => nav("/emdispatch/login", { replace: true }));
    // eslint-disable-next-line
  }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [priorityFilter, statusFilter]);
  // Auto-refresh every 15s
  useEffect(() => {
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [priorityFilter, statusFilter]);

  const grouped = useMemo(() => {
    const buckets = { RED: [], YELLOW: [], GREEN: [] };
    incidents.forEach(i => {
      const p = (i.triage_priority || "GREEN").toUpperCase();
      (buckets[p] || buckets.GREEN).push(i);
    });
    return buckets;
  }, [incidents]);

  const advance = async (inc) => {
    const next = NEXT_STATUS[inc.status];
    if (!next) return;
    try {
      const r = await emdApi.post(`/emdispatch/incidents/${inc.id}/status`, { status: next });
      toast.success(`Moved to ${STATUS_LABELS[next]}`);
      setSelected(r.data);
      load();
    } catch (e) { toast.error(formatEmdError(e)); }
  };

  return (
    <div className="min-h-screen bg-[#0F172A] text-slate-100" data-testid="emd-console">
      {/* Top bar */}
      <header className="sticky top-0 z-20 bg-[#020617]/95 backdrop-blur border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-3 flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-[#DC2626] grid place-items-center shadow-[0_6px_20px_rgba(220,38,38,0.4)]">
            <SirenIcon size={20} weight="fill" className="text-white"/>
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-heading text-lg leading-tight text-white">EmDispatch Console</div>
            <div className="text-[11px] text-white/60 truncate inline-flex items-center gap-1">
              <Hospital size={11} weight="fill"/> {me?.hospital?.name || "…"} · {me?.user?.name}
            </div>
          </div>
          <button onClick={load} disabled={loading}
                   data-testid="emd-refresh"
                   className="h-9 w-9 rounded-md bg-white/10 hover:bg-white/20 grid place-items-center">
            <ArrowClockwise size={13} className={loading ? "animate-spin" : ""}/>
          </button>
          <VoiceIntakeButton onCreated={load}/>
          <button onClick={() => setShowNew(true)}
                   data-testid="emd-new-incident"
                   className="h-9 px-3 rounded-md bg-[#DC2626] hover:bg-[#B91C1C] text-white text-xs font-bold inline-flex items-center gap-1.5">
            <Plus size={12} weight="bold"/> New call
          </button>
          <button onClick={emdLogout} data-testid="emd-logout"
                   className="h-9 w-9 rounded-md bg-white/10 hover:bg-white/20 grid place-items-center"
                   title="Sign out">
            <SignOut size={13}/>
          </button>
        </div>
      </header>

      {/* Stat strip */}
      {stats && (
        <div className="max-w-7xl mx-auto px-4 md:px-6 pt-4 grid grid-cols-2 md:grid-cols-5 gap-3">
          <StatTile label="Active total" value={stats.active_total} tone="slate"/>
          <StatTile label="RED · critical" value={stats.priorities.RED} tone="red"/>
          <StatTile label="YELLOW · urgent" value={stats.priorities.YELLOW} tone="amber"/>
          <StatTile label="GREEN · routine" value={stats.priorities.GREEN} tone="emerald"/>
          <StatTile label="Last 24 h" value={stats.last_24h_total} tone="slate"/>
        </div>
      )}

      {/* Filters */}
      <div className="max-w-7xl mx-auto px-4 md:px-6 mt-4 flex flex-wrap gap-2 items-center">
        <div className="text-[10px] uppercase tracking-[0.22em] text-white/50 font-bold pr-2">Priority</div>
        {["all", "RED", "YELLOW", "GREEN"].map(p => (
          <FilterChip key={p} on={priorityFilter === p}
                       onClick={() => setPriorityFilter(p)}
                       testid={`emd-fp-${p.toLowerCase()}`}
                       label={p === "all" ? "All" : p}/>
        ))}
        <div className="mx-2 h-4 w-px bg-white/20"/>
        <div className="text-[10px] uppercase tracking-[0.22em] text-white/50 font-bold pr-2">Status</div>
        {["active", "open", "acknowledged", "dispatched", "enroute", "arrived", "closed"].map(s => (
          <FilterChip key={s} on={statusFilter === s}
                       onClick={() => setStatusFilter(s)}
                       testid={`emd-fs-${s}`}
                       label={s === "active" ? "Active" : STATUS_LABELS[s]}/>
        ))}
      </div>

      {/* Dispatch queue */}
      <main className="max-w-7xl mx-auto px-4 md:px-6 py-5 grid grid-cols-1 md:grid-cols-3 gap-4">
        {["RED", "YELLOW", "GREEN"].map(prio => (
          <div key={prio} data-testid={`emd-col-${prio.toLowerCase()}`}
               className="bg-white/5 rounded-xl border border-white/10 overflow-hidden">
            <PriorityHeader prio={prio} count={grouped[prio]?.length || 0}/>
            <div className="p-2 space-y-2 min-h-[120px]">
              {grouped[prio].length === 0 ? (
                <div className="text-[11px] text-white/40 text-center py-6">No {prio} incidents</div>
              ) : grouped[prio].map(i => (
                <IncidentCard key={i.id} inc={i} onClick={() => setSelected(i)}/>
              ))}
            </div>
          </div>
        ))}
      </main>

      {selected && (
        <IncidentDrawer inc={selected} onClose={() => setSelected(null)}
                         onAdvance={advance}/>
      )}
      {showNew && (
        <NewIncidentSheet onClose={() => setShowNew(false)}
                            onCreated={() => { setShowNew(false); load(); }}/>
      )}
    </div>
  );
}

function StatTile({ label, value, tone }) {
  const toneMap = {
    slate:   { bg: "bg-white/5", fg: "text-white",     lb: "text-white/50" },
    red:     { bg: "bg-[#DC2626]", fg: "text-white",   lb: "text-white/70" },
    amber:   { bg: "bg-[#FBBF24]", fg: "text-[#78350F]", lb: "text-[#78350F]/80" },
    emerald: { bg: "bg-[#10B981]", fg: "text-white",   lb: "text-white/80" },
  }[tone] || {};
  return (
    <div className={`rounded-xl p-3 border border-white/10 ${toneMap.bg}`}>
      <div className={`text-[10px] uppercase tracking-widest font-bold ${toneMap.lb}`}>{label}</div>
      <div className={`font-heading text-3xl font-black leading-tight mt-1 ${toneMap.fg}`}>{value}</div>
    </div>
  );
}

function FilterChip({ on, onClick, label, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
             className={`h-7 px-2.5 text-[11px] font-bold rounded-full transition-colors ${
               on ? "bg-[#DC2626] text-white" : "bg-white/10 text-white/70 hover:bg-white/20"
             }`}>{label}</button>
  );
}

function PriorityHeader({ prio, count }) {
  const meta = PRIO[prio];
  const Icon = meta.Icon;
  return (
    <div className="flex items-center gap-2 px-3 py-2 border-b border-white/10"
         style={{ background: `${meta.bg}20` }}>
      <div className="w-7 h-7 rounded-md grid place-items-center"
           style={{ background: meta.bg, color: meta.fg }}>
        <Icon size={13} weight="fill"/>
      </div>
      <div className="font-heading text-sm font-bold" style={{ color: meta.bg }}>{meta.label}</div>
      <span className="ml-auto text-[10px] font-mono text-white/60">{count}</span>
    </div>
  );
}

function IncidentCard({ inc, onClick }) {
  const meta = PRIO[inc.triage_priority || "GREEN"] || PRIO.GREEN;
  return (
    <button onClick={onClick}
            data-testid={`emd-inc-${inc.id}`}
            className="w-full text-left bg-[#0F172A] hover:bg-[#1E293B] rounded-lg border border-white/10 p-3 transition-colors">
      <div className="flex items-center gap-2 mb-1">
        <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded"
              style={{ background: meta.bg, color: meta.fg }}>
          {inc.triage_priority || "GREEN"}
        </span>
        <span className="text-[10px] text-white/50 font-mono">{new Date(inc.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
        <span className="ml-auto text-[9px] uppercase tracking-wider text-white/60 bg-white/10 px-1.5 py-0.5 rounded">
          {STATUS_LABELS[inc.status] || inc.status}
        </span>
      </div>
      <div className="text-sm font-semibold text-white truncate">
        {inc.patient_name || "Unknown patient"}
        {inc.patient_age ? <span className="ml-1 text-white/60 font-normal">· {inc.patient_age}y</span> : null}
      </div>
      <div className="text-[12px] text-white/80 mt-0.5 line-clamp-2">{inc.chief_complaint}</div>
      {inc.address && (
        <div className="text-[11px] text-white/50 mt-1 inline-flex items-center gap-1 truncate w-full">
          <MapPin size={10}/> <span className="truncate">{inc.address}</span>
        </div>
      )}
    </button>
  );
}

function IncidentDrawer({ inc, onClose, onAdvance }) {
  const meta = PRIO[inc.triage_priority || "GREEN"] || PRIO.GREEN;
  const nextS = NEXT_STATUS[inc.status];
  return (
    <div className="fixed inset-0 z-40 bg-black/60 flex items-end sm:items-center justify-center"
          onClick={onClose} data-testid="emd-drawer">
      <div onClick={(e) => e.stopPropagation()}
           className="w-full max-w-lg bg-[#0F172A] text-white rounded-t-2xl sm:rounded-2xl border border-white/10 overflow-hidden flex flex-col"
           style={{ maxHeight: "90vh" }}>
        <div className="p-4 flex items-start gap-3 border-b border-white/10"
             style={{ background: `${meta.bg}22` }}>
          <div className="w-11 h-11 rounded-lg grid place-items-center"
               style={{ background: meta.bg, color: meta.fg }}>
            <meta.Icon size={22} weight="fill"/>
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-widest font-bold"
                 style={{ color: meta.bg }}>{meta.label}</div>
            <div className="font-heading text-lg leading-tight">
              {inc.patient_name || "Unknown patient"}
              {inc.patient_age ? <span className="ml-1 text-white/60 font-normal">· {inc.patient_age}y</span> : null}
            </div>
            <div className="text-[11px] text-white/60 mt-0.5">
              {new Date(inc.created_at).toLocaleString()} · via {inc.channel || "console"}
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 grid place-items-center">
            <X size={13}/>
          </button>
        </div>

        <div className="p-4 space-y-4 overflow-auto flex-1">
          <Section title="Chief complaint">
            <div className="text-sm text-white/90">{inc.chief_complaint}</div>
            {inc.symptoms?.length > 0 && (
              <div className="mt-1.5 flex flex-wrap gap-1">
                {inc.symptoms.map((s, i) => (
                  <span key={i} className="text-[10px] px-1.5 py-0.5 rounded bg-white/10 text-white/80">
                    {s}
                  </span>
                ))}
              </div>
            )}
          </Section>

          <Section title="AI triage">
            <div className="text-[12px] text-white/85">{inc.triage_summary || "—"}</div>
            <div className="mt-1 text-[12px] text-white/70">
              <b className="text-[#DC2626]">Recommendation:</b> {inc.triage_recommendation || "—"}
            </div>
            {inc.triage_source && (
              <div className="mt-1 text-[10px] text-white/40">
                Source: {inc.triage_source === "llm" ? "LLM (Gemini Flash)" : "Keyword heuristic"}
              </div>
            )}
          </Section>

          <Section title="Contact & location">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-[12px]">
              {inc.patient_phone && (
                <a href={`tel:${inc.patient_phone}`}
                   data-testid="emd-inc-call"
                   className="inline-flex items-center gap-2 h-9 px-3 rounded-md bg-[#DC2626] text-white font-semibold">
                  <Phone size={13} weight="fill"/> {inc.patient_phone}
                </a>
              )}
              {inc.address && (
                <div className="inline-flex items-start gap-2 text-white/80">
                  <MapPin size={13} className="mt-0.5"/>
                  <span>{inc.address}</span>
                </div>
              )}
            </div>
          </Section>

          {inc.timeline?.length > 0 && (
            <Section title="Timeline">
              <ol className="space-y-1.5">
                {inc.timeline.map((t) => (
                  <li key={t.id} className="flex items-start gap-2 text-[12px] text-white/80">
                    <Clock size={11} className="mt-1 text-white/40"/>
                    <div>
                      <b>{STATUS_LABELS[t.status] || t.status}</b>
                      <span className="text-white/50"> · {new Date(t.at).toLocaleTimeString()} · by {t.by_name || "—"}</span>
                      {t.note && <div className="text-white/60">{t.note}</div>}
                    </div>
                  </li>
                ))}
              </ol>
            </Section>
          )}
        </div>

        <div className="p-3 border-t border-white/10 flex flex-wrap gap-2 bg-black/30">
          {nextS ? (
            <button onClick={() => onAdvance(inc)}
                     data-testid="emd-inc-advance"
                     className="flex-1 h-11 rounded-md bg-[#DC2626] hover:bg-[#B91C1C] text-white font-bold text-sm inline-flex items-center justify-center gap-1.5">
              <CheckCircle size={13} weight="bold"/> {NEXT_LABEL[nextS]}
            </button>
          ) : (
            <div className="flex-1 text-center text-[12px] text-white/50 py-3">
              Incident is {STATUS_LABELS[inc.status]}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.22em] text-white/40 font-bold mb-1.5">{title}</div>
      {children}
    </div>
  );
}

function NewIncidentSheet({ onClose, onCreated }) {
  const [f, setF] = useState({
    patient_name: "", patient_age: "", patient_gender: "", patient_phone: "",
    chief_complaint: "", symptoms: "", address: "", landmark: "",
    source: "call",
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF(s => ({ ...s, [k]: v }));

  const submit = async () => {
    if ((f.chief_complaint || "").trim().length < 2) {
      toast.error("Enter the chief complaint");
      return;
    }
    const payload = {
      ...f,
      patient_age: f.patient_age ? Number(f.patient_age) : null,
      symptoms: (f.symptoms || "").split(",").map(s => s.trim()).filter(Boolean),
    };
    Object.keys(payload).forEach(k => (payload[k] === "" || payload[k] === null) && delete payload[k]);
    setBusy(true);
    try {
      const r = await emdApi.post("/emdispatch/incidents", payload);
      toast.success(`Incident triaged: ${r.data.triage_priority}`);
      onCreated && onCreated();
    } catch (e) { toast.error(formatEmdError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center overflow-auto"
         onClick={onClose} data-testid="emd-new-sheet">
      <div onClick={(e) => e.stopPropagation()}
           className="w-full max-w-lg bg-[#0F172A] text-white rounded-t-2xl sm:rounded-2xl border border-white/10 flex flex-col my-4"
           style={{ maxHeight: "94vh" }}>
        <div className="p-4 flex items-center gap-3 border-b border-white/10">
          <div className="w-10 h-10 rounded-lg bg-[#DC2626] grid place-items-center">
            <ChatCircleText size={18} weight="fill"/>
          </div>
          <div className="flex-1">
            <div className="font-heading text-base leading-tight">Guided call intake</div>
            <div className="text-[11px] text-white/60">Fill in what the caller says — AI classifies on submit</div>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 grid place-items-center">
            <X size={13}/>
          </button>
        </div>

        <div className="p-4 space-y-3 overflow-auto flex-1">
          <FGroup>
            <FldRow>
              <Fld label="Patient name" v={f.patient_name} on={(v) => set("patient_name", v)}
                    icon={UserCircle} testid="emd-f-name"/>
              <Fld label="Age" v={f.patient_age} on={(v) => set("patient_age", v)}
                    type="number" testid="emd-f-age"/>
            </FldRow>
            <FldRow>
              <Fld label="Gender" v={f.patient_gender} on={(v) => set("patient_gender", v)}
                    testid="emd-f-gender"/>
              <Fld label="Phone" v={f.patient_phone} on={(v) => set("patient_phone", v)}
                    icon={Phone} testid="emd-f-phone"/>
            </FldRow>
          </FGroup>
          <Fld wide label="Chief complaint *" v={f.chief_complaint}
                on={(v) => set("chief_complaint", v)}
                icon={FirstAid} testid="emd-f-complaint"
                placeholder="e.g. Chest pain radiating to left arm, sweating, breathless"/>
          <Fld wide label="Symptoms (comma-separated)" v={f.symptoms}
                on={(v) => set("symptoms", v)} testid="emd-f-symptoms"
                placeholder="e.g. sweating, breathless, dizziness"/>
          <Fld wide label="Address" v={f.address}
                on={(v) => set("address", v)} icon={MapPin} testid="emd-f-address"
                placeholder="Door / street / area / pincode"/>
          <Fld wide label="Landmark" v={f.landmark}
                on={(v) => set("landmark", v)} testid="emd-f-landmark"/>
        </div>

        <div className="p-3 border-t border-white/10 flex gap-2 bg-black/30">
          <button onClick={onClose} className="h-11 px-4 rounded-md bg-white/10 text-white text-sm">
            Cancel
          </button>
          <button onClick={submit} disabled={busy}
                   data-testid="emd-submit"
                   className="flex-1 h-11 rounded-md bg-[#DC2626] hover:bg-[#B91C1C] disabled:opacity-50 text-white font-bold text-sm inline-flex items-center justify-center gap-1.5">
            {busy ? "Triaging…" : (<><Truck size={13} weight="bold"/> Log & AI-triage</>)}
          </button>
        </div>
      </div>
    </div>
  );
}

function FGroup({ children }) { return <div className="space-y-2">{children}</div>; }

/* ── Voice intake — records mic audio for up to 30s and uploads to Whisper ── */
function VoiceIntakeButton({ onCreated }) {
  const [state, setState] = useState("idle"); // idle | recording | uploading
  const [seconds, setSeconds] = useState(0);
  const recRef = React.useRef(null);
  const chunksRef = React.useRef([]);
  const timerRef = React.useRef(null);
  const MAX = 30;

  const stop = React.useCallback(async () => {
    if (recRef.current && recRef.current.state !== "inactive") {
      recRef.current.stop();
    }
    if (timerRef.current) clearInterval(timerRef.current);
  }, []);

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream, { mimeType: "audio/webm" });
      chunksRef.current = [];
      rec.ondataavailable = (e) => e.data && chunksRef.current.push(e.data);
      rec.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        if (blob.size < 1000) {
          toast.error("Recording too short");
          setState("idle"); setSeconds(0); return;
        }
        setState("uploading");
        try {
          const fd = new FormData();
          fd.append("file", blob, `voice-intake-${Date.now()}.webm`);
          const r = await emdApi.post("/emdispatch/incidents/voice-intake", fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          toast.success(`AI triaged as ${r.data.triage_priority}`);
          onCreated && onCreated();
        } catch (e) { toast.error(formatEmdError(e)); }
        finally { setState("idle"); setSeconds(0); }
      };
      recRef.current = rec;
      rec.start();
      setState("recording");
      setSeconds(0);
      timerRef.current = setInterval(() => {
        setSeconds(s => {
          const next = s + 1;
          if (next >= MAX) { stop(); return MAX; }
          return next;
        });
      }, 1000);
    } catch (e) {
      toast.error(e.message || "Could not access microphone");
    }
  };

  const label =
    state === "recording" ? `Stop · ${seconds}s / ${MAX}s`
    : state === "uploading" ? "Transcribing…"
    : "Voice intake";

  return (
    <button
      onClick={state === "recording" ? stop : state === "idle" ? start : undefined}
      disabled={state === "uploading"}
      data-testid="emd-voice-intake"
      className={`h-9 px-3 rounded-md text-xs font-bold inline-flex items-center gap-1.5 disabled:opacity-60 transition-colors ${
        state === "recording"
          ? "bg-white text-[#DC2626] ring-2 ring-[#DC2626] animate-pulse"
          : "bg-white/10 text-white hover:bg-white/20"
      }`}
      title="Record a 30-sec voice note — Whisper transcribes & AI triages"
    >
      {state === "recording" ? <Stop size={12} weight="fill"/> : <Microphone size={12} weight="fill"/>}
      {label}
    </button>
  );
}
function FGroupEnd() { return null; }
function FldRow({ children }) { return <div className="grid grid-cols-2 gap-2">{children}</div>; }
function Fld({ label, v, on, type = "text", placeholder, wide, icon: Icon, testid }) {
  return (
    <label className={wide ? "block" : "block"}>
      <div className="text-[10px] uppercase tracking-widest text-white/50 font-bold mb-1">{label}</div>
      <div className="relative">
        {Icon && <Icon size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40"/>}
        <input type={type} value={v} onChange={(e) => on(e.target.value)}
                data-testid={testid}
                placeholder={placeholder}
                className={`w-full h-10 ${Icon ? "pl-9" : "pl-3"} pr-3 rounded-md bg-white/5 border border-white/10 focus:border-[#DC2626] outline-none text-sm text-white placeholder:text-white/30`}/>
      </div>
    </label>
  );
}
